import { AddButton } from "@/components/ui/AddButton";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Fragment, useCallback, useState } from "react";
import type { ReactNode } from "react";
import type {
  FigureLayoutResponse,
  FigureResponse,
} from "@patent-studio/api-types";
import { useEscapeScope } from "@/components/ui/escape-scope";
import { isPending } from "@/hooks/use-figures";
import { ROOT } from "@/hooks/use-figure-layout";
import { cn } from "@/lib/utils";
import {
  ArrangementDrag,
  topLevelEntries,
  useTopLevelDrop,
} from "@/components/shared/arrangement-drag";
import type { ArrangementView } from "@/components/shared/arrangement-drag";
import { sortedByFileName } from "@/components/shared/arrangement-drag";
import { toast } from "sonner";
import { namedGroup } from "@/lib/figure-group-naming";
import { FigureGroupLane } from "./FigureGroupLane";
import { SortableFigureRow, StaticFigureRow } from "./FigureRow";
import { named } from "@/lib/figure-naming";
import { announceDeleted, figureName } from "@/v2/figures/DeletedFigures";

/**
 * A place a new group can go, offered as a control rather than as a drop.
 *
 * This is the screen's route to grouping that needs no drag at all, which is
 * what keeps the operation available to anyone the drag is not available to.
 */
function PlacementSlot({
  description,
  onPlace,
}: {
  description: string;
  onPlace: () => void;
}) {
  return (
    <li>
      <AddButton
        emphasis="brand"
        onClick={onPlace}
        aria-label={`Place the new group ${description}`}
      >
        Insert new group here
      </AddButton>
    </li>
  );
}

/**
 * The top level, as somewhere a figure can be dropped rather than only a list.
 *
 * A group is a lane inside a row, so with nothing beside it every part of the
 * list belongs to the group and a figure being taken out has nowhere to land.
 * The list accepts drops itself, above or below whatever it holds, and keeps a
 * margin of its own so there is somewhere to aim at. It yields to the rows and
 * lanes inside it, which is what makes it the target only when nothing else is
 * under the pointer.
 */
function TopLevelList({
  children,
  empty,
}: {
  children: ReactNode;
  empty: boolean;
}) {
  const root = useTopLevelDrop();

  return (
    <ul
      ref={root.ref}
      className={cn("flex flex-col gap-2 py-3", empty && "m-0 py-0")}
    >
      {children}
    </ul>
  );
}

/**
 * The ordered figure list, rearranged by dragging and grouped by placing.
 *
 * Order is the whole point of the step: the number a figure carries is its
 * position, so moving one renumbers the ones it passed and moving one into a
 * group letters it instead. That happens here on drop rather than after the
 * server answers.
 */
export function FigureOrganiser({
  taskId,
  figures,
  layout,
}: {
  taskId: string | undefined;
  figures: FigureResponse[];
  layout: FigureLayoutResponse;
}) {
  const [placing, setPlacing] = useState(false);

  const stopPlacing = useCallback(() => {
    setPlacing(false);
  }, []);

  // Escape leaves the mode, the same key that abandons a drag, and through the
  // same stack so anything opened over it takes the press first. Its own window
  // listener answered in mount order, which meant a popover opened during a
  // placement could not close without cancelling the placement as well.
  useEscapeScope("gesture", placing, stopPlacing);

  const pending = figures.filter(isPending);

  return (
    <ArrangementDrag taskId={taskId} figures={figures} layout={layout}>
      {(view) => (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Grouped figures share one number and are lettered within it.
            </span>
            <span className="flex items-center gap-2">
              <SortByFileName view={view} />
              <Button
                variant="primary"
                size="dense"
                type="button"
                aria-pressed={placing}
                aria-label={placing ? "Cancel new group" : "New group"}
                onClick={() => {
                  setPlacing(!placing);
                }}
              >
                {placing ? (
                  <>
                    <X className="size-3.5" aria-hidden="true" />
                    Cancel new group
                  </>
                ) : (
                  <>
                    <Plus className="size-3.5" aria-hidden="true" />
                    New group
                  </>
                )}
              </Button>
            </span>
          </div>

          <Arranged
            view={view}
            placing={placing}
            onPlaced={stopPlacing}
            pending={pending}
            empty={figures.length === 0}
          />
        </div>
      )}
    </ArrangementDrag>
  );
}

/**
 * Every figure in file-name order at one press, as a person counts (fig2 before fig10): the
 * fast way to order a long set that was named in order. One press undoes it.
 */
function SortByFileName({ view }: { view: ArrangementView }) {
  return (
    <Button
      variant="secondary"
      size="dense"
      type="button"
      onClick={() => {
        const before = view.lanes;
        view.acts.arrange(sortedByFileName(before, view.byId));
        toast.success("Figures sorted by file name.", {
          action: {
            label: "Undo",
            onClick: () => {
              view.acts.arrange(before);
            },
          },
        });
      }}
    >
      Sort by file name
    </Button>
  );
}

/**
 * The card's Remove: no question first, because the toast offers Undo and the figure waits in
 * Deleted Figures until it is restored or deleted for good there.
 */
function removing(view: ArrangementView) {
  return (fileId: string) => {
    const figure = view.byId.get(fileId);
    const name = figureName(figure?.label, figure?.original_filename ?? "");
    void view.acts.remove(fileId).then((gone) => {
      if (!gone) return;
      announceDeleted(
        name,
        () => view.acts.restore(fileId),
        `${name} moved to Deleted Figures.`,
      );
    });
  };
}

/** One rendered thing per top-level entry, carrying the position a group placed before it would take. */
function units(view: ArrangementView) {
  const { over, byId, acts } = view;
  const onDelete = removing(view);
  return topLevelEntries(view).map((entry) =>
    entry.kind === "group"
      ? {
          key: entry.id,
          at: entry.at,
          describe: `before ${namedGroup(entry.group)}`,
          node: (
            <FigureGroupLane
              group={{
                ...entry.group,
                members: entry.members.map((fileId) => ({
                  kind: "figure" as const,
                  file_id: fileId,
                })),
              }}
              index={entry.at}
              figures={byId}
              onDelete={onDelete}
              onRetitle={acts.retitleGroup}
              onDissolve={acts.dissolveGroup}
              over={over}
            />
          ),
        }
      : {
          key: entry.id,
          at: entry.at,
          describe: `before ${named(entry.figure)}`,
          node: (
            <SortableFigureRow
              figure={entry.figure}
              index={entry.at}
              lane={ROOT}
              onDelete={onDelete}
              landing={over === entry.id}
            />
          ),
        },
  );
}

/** The list as the drag is showing it, with the group placement slots when asked for. */
function Arranged({
  view,
  placing,
  onPlaced,
  pending,
  empty,
}: {
  view: ArrangementView;
  placing: boolean;
  onPlaced: () => void;
  pending: FigureResponse[];
  empty: boolean;
}) {
  const { lanes, acts } = view;
  const onDelete = removing(view);
  const place = (position: number) => {
    acts.newGroup(position, lanes);
    onPlaced();
  };
  return (
    <TopLevelList empty={empty}>
      {units(view).map((unit) => (
        <Fragment key={unit.key}>
          {placing && (
            <PlacementSlot
              description={unit.describe}
              onPlace={() => {
                place(unit.at);
              }}
            />
          )}
          {unit.node}
        </Fragment>
      ))}

      {placing && (
        <PlacementSlot
          description="at the end"
          onPlace={() => {
            place((lanes[ROOT] ?? []).length);
          }}
        />
      )}

      {pending.map((figure) => (
        <StaticFigureRow
          key={figure.file_id}
          figure={figure}
          onDelete={onDelete}
        />
      ))}
    </TopLevelList>
  );
}
